import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Link2, Copy, Eye, Trash2, Gift, Users, DollarSign, BarChart3 } from 'lucide-react';
import { copyText } from '@/lib/copyText';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function CampaignPage() {
  const { token, manager } = useOutletContext();
  const [campaigns, setCampaigns] = useState([]);
  const [restaurantSlug, setRestaurantSlug] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);
  const restaurantId = manager?.restaurant_id;

  const fetchCampaigns = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/gift-card-campaigns?restaurant_id=${restaurantId}`, { headers });
      const data = await res.json();
      setCampaigns(data.campaigns || []);
      if (data.restaurant_slug) setRestaurantSlug(data.restaurant_slug);
    } catch { toast.error('Failed to load campaigns'); }
  }, [restaurantId, headers]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const getStoreLink = (campaignSlug) => {
    const origin = window.location.origin;
    const rSlug = restaurantSlug || `restaurant-${restaurantId}`;
    return campaignSlug ? `${origin}/store/${rSlug}/${campaignSlug}` : `${origin}/store/${rSlug}`;
  };

  const copyLink = (campaign) => {
    copyText(getStoreLink(campaign.slug), 'Link copied!');
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    await fetch(`${API}/api/gift-card-campaigns/${id}`, { method: 'DELETE', headers });
    toast.success('Campaign deleted');
    fetchCampaigns();
  };

  return (
    <div className="space-y-6" data-testid="campaign-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Gift Card Campaigns</h2>
          <p className="text-sm text-slate-500">Create campaigns with one-click shareable links</p>
        </div>
        <div className="flex gap-2">
          {restaurantSlug && (
            <Button variant="outline" onClick={() => window.open(getStoreLink(), '_blank')} data-testid="view-storefront-btn">
              <Eye className="h-4 w-4 mr-2" /> View Storefront
            </Button>
          )}
          <Button onClick={() => setShowCreate(true)} data-testid="create-campaign-btn">
            <Plus className="h-4 w-4 mr-2" /> New Campaign
          </Button>
        </div>
      </div>

      {/* Campaign Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {campaigns.length === 0 && (
          <div className="col-span-2 text-center py-12 text-slate-400 border-2 border-dashed rounded-xl">
            <Gift className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No campaigns yet. Create your first one!</p>
          </div>
        )}
        {campaigns.map((c) => (
          <div key={c.id} className="border rounded-xl p-4 hover:shadow-md transition" data-testid={`campaign-${c.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold text-lg">{c.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  c.campaign_type === 'free' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
                }`}>{c.campaign_type === 'free' ? 'Free Giveaway' : 'Paid'}</span>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {c.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            {c.description && <p className="text-sm text-slate-500 mb-3 line-clamp-2">{c.description}</p>}

            <div className="grid grid-cols-3 gap-2 mb-3 text-center">
              <div className="bg-slate-50 rounded-lg p-2">
                <DollarSign className="h-4 w-4 mx-auto text-emerald-500 mb-1" />
                <div className="text-lg font-bold">{fmt(c.gift_card_amount)}</div>
                <div className="text-xs text-slate-400">Amount</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <Users className="h-4 w-4 mx-auto text-blue-500 mb-1" />
                <div className="text-lg font-bold">{c.cards_claimed}/{c.quantity_limit}</div>
                <div className="text-xs text-slate-400">Claimed</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2">
                <BarChart3 className="h-4 w-4 mx-auto text-purple-500 mb-1" />
                <div className="text-lg font-bold">{c.total_views}</div>
                <div className="text-xs text-slate-400">Views</div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" onClick={() => copyLink(c)} data-testid={`copy-link-${c.id}`}>
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy Link
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowDetail(c)} data-testid={`view-campaign-${c.id}`}>
                <Eye className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteCampaign(c.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <CreateCampaignDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={fetchCampaigns} restaurantId={restaurantId} headers={headers} />
      {showDetail && <CampaignDetailDialog campaign={showDetail} onClose={() => setShowDetail(null)} headers={headers} />}
    </div>
  );
}

function CreateCampaignDialog({ open, onClose, onCreated, restaurantId, headers }) {
  const [form, setForm] = useState({
    name: '', campaign_type: 'paid', gift_card_amount: '25', quantity_limit: '100', description: ''
  });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Enter campaign name'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/gift-card-campaigns`, {
        method: 'POST', headers,
        body: JSON.stringify({
          restaurant_id: restaurantId, name: form.name, campaign_type: form.campaign_type,
          gift_card_amount: Number(form.gift_card_amount), quantity_limit: Number(form.quantity_limit),
          description: form.description,
        }),
      });
      const data = await res.json();
      if (data.success) { setResult(data); onCreated(); toast.success('Campaign created!'); }
      else toast.error(data.detail || 'Failed');
    } catch { toast.error('Network error'); }
    finally { setCreating(false); }
  };

  const handleClose = () => { setResult(null); setForm({ name: '', campaign_type: 'paid', gift_card_amount: '25', quantity_limit: '100', description: '' }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="create-campaign-dialog">
        <DialogHeader><DialogTitle>{result ? 'Campaign Created!' : 'Create Campaign'}</DialogTitle></DialogHeader>
        {result ? (
          <div className="space-y-4 text-center py-4">
            <Link2 className="h-12 w-12 mx-auto text-emerald-500" />
            <div className="text-lg font-bold">{result.campaign.name}</div>
            <div className="bg-slate-100 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-1">Share this link:</p>
              <p className="font-mono text-sm break-all text-blue-600">{`${window.location.origin}/store/${result.public_link}`}</p>
            </div>
            <Button onClick={() => copyText(`${window.location.origin}/store/${result.public_link}`, 'Copied!')} className="w-full" data-testid="copy-campaign-link">
              <Copy className="h-4 w-4 mr-2" /> Copy Link
            </Button>
            <Button variant="outline" onClick={() => window.open(`${window.location.origin}/store/${result.public_link}`, '_blank')} className="w-full" data-testid="view-campaign-live">
              <Eye className="h-4 w-4 mr-2" /> View Live
            </Button>
            <Button variant="outline" onClick={handleClose} className="w-full">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Campaign Name *</label>
              <Input placeholder="e.g. Holiday Gift Cards" value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="campaign-name-input" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Type</label>
              <div className="flex gap-2">
                {['paid', 'free'].map(t => (
                  <Button key={t} size="sm" variant={form.campaign_type === t ? 'default' : 'outline'} className="flex-1"
                    onClick={() => setForm(f => ({ ...f, campaign_type: t }))} data-testid={`type-${t}`}>
                    {t === 'paid' ? 'Paid (Customer Buys)' : 'Free Giveaway'}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium block mb-1">Card Amount ($)</label>
                <Input type="number" min="1" value={form.gift_card_amount}
                  onChange={(e) => setForm(f => ({ ...f, gift_card_amount: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium block mb-1">Quantity Limit</label>
                <Input type="number" min="1" value={form.quantity_limit}
                  onChange={(e) => setForm(f => ({ ...f, quantity_limit: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Description</label>
              <Input placeholder="Brief description for the campaign page" value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <Button onClick={handleCreate} disabled={creating} className="w-full" data-testid="create-campaign-submit">
              {creating ? 'Creating...' : 'Create & Generate Link'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CampaignDetailDialog({ campaign, onClose, headers }) {
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/gift-card-campaigns/${campaign.id}`, { headers })
      .then(r => r.json()).then(setDetail).catch(() => {});
  }, [campaign.id, headers]);

  return (
    <Dialog open={!!campaign} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="campaign-detail-dialog">
        <DialogHeader><DialogTitle>Campaign Details</DialogTitle></DialogHeader>
        {detail ? (
          <div className="space-y-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <h3 className="text-xl font-bold">{detail.campaign.name}</h3>
              <div className="text-2xl font-bold text-emerald-600 mt-1">{fmt(detail.campaign.gift_card_amount)}</div>
              <div className="text-sm text-slate-500">{detail.campaign.cards_claimed} / {detail.campaign.quantity_limit} claimed</div>
              {detail.campaign.total_revenue > 0 && (
                <div className="text-sm text-blue-600 mt-1">Revenue: {fmt(detail.campaign.total_revenue)}</div>
              )}
            </div>

            {detail.purchases?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Recent Claims/Purchases</h4>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {detail.purchases.map((p) => (
                    <div key={p.id} className="flex justify-between text-sm p-2 bg-slate-50 rounded">
                      <span>{p.buyer_name} ({p.buyer_phone})</span>
                      <span className="font-medium">{fmt(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">Loading...</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
